/**
 * Region-aware backend URL selection.
 *
 * In production (Vercel), API requests go through Vercel CDN reverse-proxy
 * to avoid direct cross-ocean connections from client browsers to Cloud Run.
 *
 * Asian users are routed to the asia-east1 Cloud Run instance;
 * everyone else to the us-central1 instance.
 */

const ASIA_TIMEZONES_PREFIX = [
  "Asia/",
  "Pacific/Auckland", // NZ is closer to Asia region
];

const PRODUCTION_HOSTS = [
  "ideawiv.com",
  "www.ideawiv.com",
  "vercel.app",
];

let _cached: string | null = null;

function isAsiaUser(): boolean {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return ASIA_TIMEZONES_PREFIX.some((p) => tz.startsWith(p));
  } catch {
    return false;
  }
}

function isProductionHost(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  const host = window.location.hostname;
  return PRODUCTION_HOSTS.some(
    (h) => host === h || host.endsWith(`.${h}`),
  );
}

/**
 * Return the backend base URL for API calls.
 *
 * - Production (Vercel): `/api/backend` or `/api/backend-asia` (same-origin proxy)
 * - Local dev: `VITE_APP_AI_BACKEND` env var (direct URL)
 */
export function getBackendUrl(): string {
  if (_cached !== null) {
    return _cached;
  }

  if (isProductionHost()) {
    _cached = isAsiaUser() ? "/api/backend-asia" : "/api/backend";
    return _cached;
  }

  // Local development — use env var directly
  _cached =
    (import.meta.env.VITE_APP_AI_BACKEND as string) || "http://localhost:8000";
  return _cached;
}

/**
 * Reset cached URL (for testing or after region change).
 */
export function resetBackendUrlCache(): void {
  _cached = null;
}
